import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = 'http://localhost:5000';

function ProfilePage({ token, onBack }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Edit profile form state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Change password form state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const userData = response.data.user;
      setUser(userData);
      setEditName(userData.name);
      setEditEmail(userData.email);
      setError('');
    } catch (err) {
      console.error('Fetch profile error:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleColor = () => {
    switch (user?.role) {
      case 'admin':
        return '#e74c3c';
      case 'facilitator':
        return '#3498db';
      case 'contributor':
        return '#27ae60';
      default:
        return '#95a5a6';
    }
  };

  const handleEditProfile = async (e) => {
    e.preventDefault();
    setEditError('');
    setSuccessMessage('');

    // Validation
    if (!editName || !editEmail) {
      setEditError('Name and email are required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editEmail)) {
      setEditError('Invalid email format');
      return;
    }

    try {
      setEditLoading(true);
      const response = await axios.put(
        `${API_URL}/api/users/me`,
        {
          name: editName,
          email: editEmail,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setUser(response.data.user);
      setSuccessMessage('Profile updated successfully!');
      setIsEditingProfile(false);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to update profile';
      setEditError(message);
    } finally {
      setEditLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setSuccessMessage('');

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    try {
      setPasswordLoading(true);
      await axios.put(
        `${API_URL}/api/users/me/password`,
        {
          currentPassword,
          newPassword,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccessMessage('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordForm(false);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to change password';
      setPasswordError(message);
    } finally {
      setPasswordLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-page-container">
        <div className="loading">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="profile-page-container">
      <div className="profile-page">
        <button className="back-btn" onClick={onBack}>
          ← Back
        </button>

        <div className="profile-header">
          <div className="profile-avatar-large">{user?.name.charAt(0)}</div>
          <div className="profile-header-info">
            <h1>{user?.name}</h1>
            <div className="profile-role" style={{ color: getRoleColor() }}>
              {user?.role.charAt(0).toUpperCase() + user?.role.slice(1)}
            </div>
          </div>
        </div>

        {error && <div className="error-banner">{error}</div>}
        {successMessage && <div className="success-banner">{successMessage}</div>}

        <div className="profile-content">
          {/* Account Information */}
          <section className="profile-section">
            <div className="section-header">
              <h2>Account Information</h2>
              {!isEditingProfile && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setIsEditingProfile(true)}
                >
                  Edit Profile
                </button>
              )}
            </div>

            {isEditingProfile ? (
              <form onSubmit={handleEditProfile} className="edit-profile-form">
                {editError && <div className="form-error">{editError}</div>}

                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={editLoading}
                  >
                    {editLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setIsEditingProfile(false);
                      setEditName(user?.name);
                      setEditEmail(user?.email);
                      setEditError('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="info-grid">
                <div className="info-item">
                  <label>Full Name</label>
                  <p className="info-value">{user?.name}</p>
                </div>
                <div className="info-item">
                  <label>Email</label>
                  <p className="info-value">{user?.email}</p>
                </div>
                <div className="info-item">
                  <label>Role</label>
                  <p className="info-value" style={{ color: getRoleColor(), fontWeight: '600' }}>
                    {user?.role.charAt(0).toUpperCase() + user?.role.slice(1)}
                  </p>
                </div>
                <div className="info-item">
                  <label>Account Status</label>
                  <p className="info-value">
                    <span style={{ color: user?.is_active ? '#27ae60' : '#e74c3c' }}>
                      {user?.is_active ? '✓ Active' : '✗ Inactive'}
                    </span>
                  </p>
                </div>
                <div className="info-item">
                  <label>Member Since</label>
                  <p className="info-value">{formatDate(user?.created_at)}</p>
                </div>
              </div>
            )}
          </section>

          {/* Security Section */}
          <section className="profile-section">
            <div className="section-header">
              <h2>Security</h2>
              {!showPasswordForm && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowPasswordForm(true)}
                >
                  Change Password
                </button>
              )}
            </div>

            {showPasswordForm && (
              <form onSubmit={handleChangePassword} className="password-form">
                {passwordError && <div className="form-error">{passwordError}</div>}

                <div className="form-group">
                  <label>Current Password</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter your current password"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 8 characters)"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={passwordLoading}
                  >
                    {passwordLoading ? 'Updating...' : 'Update Password'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowPasswordForm(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordError('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {!showPasswordForm && (
              <p className="security-info">
                🔐 Keep your password strong and unique. Change it regularly to maintain account security.
              </p>
            )}
          </section>
        </div>
      </div>

      <style>{`
        .profile-page-container {
          width: 100%;
          padding: 20px;
          background: #f5f5f5;
          min-height: 100vh;
        }

        .profile-page {
          max-width: 700px;
          margin: 0 auto;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .back-btn {
          padding: 20px;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 14px;
          color: #3498db;
          font-weight: 500;
          transition: color 0.2s;
        }

        .back-btn:hover {
          color: #2980b9;
        }

        .profile-header {
          display: flex;
          gap: 20px;
          padding: 30px 20px;
          background: linear-gradient(135deg, #8FC5B2 0%, #6ba99f 100%);
          color: white;
          align-items: center;
        }

        .profile-avatar-large {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          font-size: 32px;
          font-weight: 700;
          flex-shrink: 0;
        }

        .profile-header-info h1 {
          margin: 0;
          font-size: 24px;
          font-weight: 700;
        }

        .profile-role {
          font-size: 14px;
          font-weight: 500;
          margin-top: 4px;
        }

        .profile-content {
          padding: 30px;
        }

        .profile-section {
          margin-bottom: 30px;
        }

        .profile-section h2 {
          margin: 0 0 20px 0;
          font-size: 16px;
          color: #2c3e50;
          border-bottom: 2px solid #8FC5B2;
          padding-bottom: 10px;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .section-header h2 {
          margin: 0;
          border: none;
          padding: 0;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 20px;
        }

        .info-item {
          display: flex;
          flex-direction: column;
        }

        .info-item label {
          font-size: 12px;
          font-weight: 600;
          color: #7f8c8d;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 6px;
        }

        .info-value {
          font-size: 16px;
          color: #2c3e50;
          word-break: break-all;
        }

        .password-form {
          background: #f9f9f9;
          padding: 20px;
          border-radius: 8px;
          border-left: 4px solid #8FC5B2;
        }

        .edit-profile-form {
          background: #f9f9f9;
          padding: 20px;
          border-radius: 8px;
          border-left: 4px solid #8FC5B2;
        }

        .form-group {
          margin-bottom: 15px;
        }

        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          color: #2c3e50;
          font-size: 14px;
        }

        .form-group input {
          width: 100%;
          padding: 10px;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          font-size: 14px;
          box-sizing: border-box;
          transition: border-color 0.2s;
        }

        .form-group input:focus {
          outline: none;
          border-color: #8FC5B2;
          box-shadow: 0 0 0 2px rgba(143, 197, 178, 0.1);
        }

        .form-error {
          background: #fadbd8;
          color: #c0392b;
          padding: 10px;
          border-radius: 4px;
          margin-bottom: 15px;
          font-size: 14px;
        }

        .form-actions {
          display: flex;
          gap: 10px;
          margin-top: 20px;
        }

        .btn {
          padding: 10px 16px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
          font-size: 14px;
          transition: all 0.2s;
        }

        .btn-primary {
          background: #8FC5B2;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #7ab39f;
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-secondary {
          background: #e8e8e8;
          color: #2c3e50;
        }

        .btn-secondary:hover {
          background: #d8d8d8;
        }

        .security-info {
          color: #7f8c8d;
          font-size: 14px;
          line-height: 1.6;
          margin: 0;
          padding: 12px;
          background: #f0f8f6;
          border-radius: 4px;
          border-left: 3px solid #8FC5B2;
        }

        .error-banner {
          background: #fadbd8;
          border: 1px solid #f5b7b1;
          color: #c0392b;
          padding: 12px;
          border-radius: 4px;
          margin-bottom: 20px;
          font-size: 14px;
        }

        .success-banner {
          background: #d5f4e6;
          border: 1px solid #a9dfbf;
          color: #186a3b;
          padding: 12px;
          border-radius: 4px;
          margin-bottom: 20px;
          font-size: 14px;
        }

        .loading {
          padding: 40px;
          text-align: center;
          color: #7f8c8d;
          font-size: 16px;
        }

        @media (max-width: 600px) {
          .profile-header {
            flex-direction: column;
            text-align: center;
            padding: 20px;
          }

          .info-grid {
            grid-template-columns: 1fr;
          }

          .section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .section-header .btn {
            width: 100%;
          }

          .form-actions {
            flex-direction: column;
          }

          .form-actions .btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}

export default ProfilePage;
